import { useState, useMemo } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Wallet, Plus, Search, MoreHorizontal, Check, X, Edit3, Eye, Ban, Trash2,
  ArrowUpCircle, ArrowDownCircle, FileText, ExternalLink, Upload, Sparkles, Loader2,
  ChevronDown,
} from 'lucide-react';

/* ---------- types ---------- */
interface CashflowItem {
  id: string;
  entity_id: string;
  entity_name: string;
  type: string;
  category: string;
  description: string | null;
  amount: number;
  currency: string;
  expected_date: string;
  actual_date: string | null;
  bank_account_id: string | null;
  bank_account_name: string | null;
  counterparty: string | null;
  status: string;
  document_name: string | null;
  document_url: string | null;
  notes: string | null;
  created_by: string | null;
  approved_by: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  payment_method: string | null;
  is_intercompany: boolean;
  intercompany_entity_id: string | null;
  source_tag: string | null;
  actual_amount: number | null;
  actual_currency: string | null;
  invoice_parsed_data: Record<string, unknown> | null;
}

interface CashflowItemsData {
  items: CashflowItem[];
  summary: {
    planned_total: number;
    approved_total: number;
    completed_total: number;
    net: number;
    total_revenue: number;
    total_expenses: number;
    count: number;
  };
}

interface Entity { id: string; code: string; name: string; }
interface BankAccount { id: string; entity_id: string; bank_name: string; currency: string; }
interface Category { id: string; name: string; type: string; is_active: boolean; }

/* ---------- helpers ---------- */
const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'AED', 'USDT', 'USDC'];

// formatCurrency provided via useCurrency — use useTxnFmt inside components
function useTxnFmt() {
  const { fmt } = useCurrency();
  return {
    formatCurrency: (amount: number, currency = 'EUR') => fmt(amount, currency),
    formatRaw: (amount: number) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount),
  };
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ────────── Date range presets ──────────

   YTD is the default shown first — opens with Jan 1 → today so
   treasury immediately sees what's booked this year. All other
   presets below are one click away from the Select. 'custom' is
   a sentinel used by the UI when the user manually edits either
   date input, so the Select switches to "Custom" automatically
   instead of fighting the user's edits.                          */

type DateRangePresetKey =
  | 'ytd' | 'this_month' | 'last_month' | 'next_month'
  | 'this_quarter' | 'last_quarter'
  | 'this_year' | 'last_year' | 'next_year'
  | 'last_30' | 'last_90'
  | 'all' | 'custom';

const DATE_RANGE_PRESETS: { key: DateRangePresetKey; label: string }[] = [
  { key: 'ytd',          label: 'Year to date' },
  { key: 'this_month',   label: 'This month' },
  { key: 'last_month',   label: 'Last month' },
  { key: 'next_month',   label: 'Next month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'this_year',    label: 'This year' },
  { key: 'last_year',    label: 'Last year' },
  { key: 'next_year',    label: 'Next year' },
  { key: 'last_30',      label: 'Last 30 days' },
  { key: 'last_90',      label: 'Last 90 days' },
  { key: 'all',          label: 'All time' },
  { key: 'custom',       label: 'Custom…' },
];

// Local-time YYYY-MM-DD (avoid toISOString which shifts to UTC and can
// put a "this month" boundary on the wrong day depending on the hour).
function ymd(d: Date): string {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function computeRange(key: DateRangePresetKey): { from: string; to: string } {
  if (key === 'all' || key === 'custom') return { from: '', to: '' };
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case 'ytd':
      return { from: ymd(new Date(y, 0, 1)), to: ymd(now) };
    case 'this_month':
      return { from: ymd(new Date(y, m, 1)), to: ymd(now) };
    case 'last_month':
      return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    case 'next_month':
      return { from: ymd(new Date(y, m + 1, 1)), to: ymd(new Date(y, m + 2, 0)) };
    case 'this_quarter': {
      const q = Math.floor(m / 3);
      return { from: ymd(new Date(y, q * 3, 1)), to: ymd(now) };
    }
    case 'last_quarter': {
      const q = Math.floor(m / 3);
      // Rolls back to Q4 of the previous year when we're in Q1 now.
      const startYear = q === 0 ? y - 1 : y;
      const startMonth = q === 0 ? 9 : (q - 1) * 3;
      const endYear = q === 0 ? y - 1 : y;
      const endMonth = q === 0 ? 12 : q * 3;
      return {
        from: ymd(new Date(startYear, startMonth, 1)),
        to: ymd(new Date(endYear, endMonth, 0)),
      };
    }
    case 'this_year':
      return { from: ymd(new Date(y, 0, 1)), to: ymd(new Date(y, 11, 31)) };
    case 'last_year':
      return { from: ymd(new Date(y - 1, 0, 1)), to: ymd(new Date(y - 1, 11, 31)) };
    case 'next_year':
      return { from: ymd(new Date(y + 1, 0, 1)), to: ymd(new Date(y + 1, 11, 31)) };
    case 'last_30': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { from: ymd(start), to: ymd(now) };
    }
    case 'last_90': {
      const start = new Date(now);
      start.setDate(start.getDate() - 90);
      return { from: ymd(start), to: ymd(now) };
    }
    default:
      return { from: '', to: '' };
  }
}

const STATUS_STYLES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  planned: { variant: 'secondary', className: 'bg-amber-100 text-amber-800 hover:bg-amber-100' },
  approved: { variant: 'secondary', className: 'bg-blue-100 text-blue-800 hover:bg-blue-100' },
  completed: { variant: 'secondary', className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' },
  cancelled: { variant: 'secondary', className: 'bg-gray-100 text-gray-500 hover:bg-gray-100' },
};

const SOURCE_TAG_STYLES: Record<string, { label: string; className: string }> = {
  budget_2026: { label: 'Budget', className: 'bg-blue-100 text-blue-700' },
  adjusted: { label: 'Adjusted', className: 'bg-amber-100 text-amber-700' },
  unplanned: { label: 'Unplanned', className: 'bg-red-100 text-red-700' },
};

/* ---------- main component ---------- */
const ALL_STATUSES = ['planned', 'approved', 'completed', 'cancelled'] as const;
const ALL_TYPES = ['revenue', 'expense'] as const;

export default function CashFlow() {
  const { formatCurrency } = useTxnFmt();
  // Multi-select filters: all checked by default — untick to exclude
  const [selectedEntities, setSelectedEntities] = useState<Set<string> | null>(null); // null = not yet initialized
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set(ALL_STATUSES));
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set(ALL_TYPES));
  const [selectedCategories, setSelectedCategories] = useState<Set<string> | null>(null); // null = not yet initialized
  // Date range state: `datePreset` drives `dateFrom`/`dateTo` via
  // computeRange(). Default is YTD so treasury sees the current year's
  // bookings first. Manual edits to either date input flip the preset
  // back to 'custom' so the Select stays in sync with the user.
  const [datePreset, setDatePreset] = useState<DateRangePresetKey>('ytd');
  const [dateFrom, setDateFrom] = useState<string>(() => computeRange('ytd').from);
  const [dateTo, setDateTo] = useState<string>(() => computeRange('ytd').to);
  const [searchTerm, setSearchTerm] = useState('');

  const applyDatePreset = (key: DateRangePresetKey) => {
    setDatePreset(key);
    if (key === 'custom') return;               // leave from/to as-is
    const { from, to } = computeRange(key);
    setDateFrom(from);
    setDateTo(to);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'expense' | 'revenue'>('expense');
  const [editingItem, setEditingItem] = useState<CashflowItem | null>(null);
  const [detailItem, setDetailItem] = useState<CashflowItem | null>(null);
  const [completionItem, setCompletionItem] = useState<CashflowItem | null>(null);

  // Build query params — only date + search go server-side; multi-selects filter client-side
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  if (searchTerm) params.set('search', searchTerm);

  const url = `/api/fin/cashflow-items${params.toString() ? '?' + params.toString() : ''}`;
  const { data: rawData, loading, refresh } = useFetch<CashflowItemsData>(url);
  const { data: entities } = useFetch<Entity[]>('/api/fin/entities');
  const { data: categories } = useFetch<Category[]>('/api/fin/categories');

  // Unique categories for filter
  const uniqueCategories = useMemo(() => {
    if (!categories) return [];
    return categories.map(c => c.name);
  }, [categories]);

  // Build option lists for multi-select popovers
  const entityOptions = useMemo(() => (entities || []).map(e => ({ value: e.id, label: `${e.code} - ${e.name}` })), [entities]);
  const categoryOptions = useMemo(() => uniqueCategories.map(c => ({ value: c, label: c })), [uniqueCategories]);

  // Lazy-initialize entity & category sets to "all selected" once options arrive
  const effectiveEntities = useMemo(() => {
    if (selectedEntities !== null) return selectedEntities;
    return new Set(entityOptions.map(o => o.value));
  }, [selectedEntities, entityOptions]);

  const effectiveCategories = useMemo(() => {
    if (selectedCategories !== null) return selectedCategories;
    return new Set(categoryOptions.map(o => o.value));
  }, [selectedCategories, categoryOptions]);

  // Client-side multi-select filtering
  const data = useMemo((): CashflowItemsData | null => {
    if (!rawData) return null;
    const items = rawData.items.filter(item => {
      if (effectiveEntities.size > 0 && !effectiveEntities.has(item.entity_id)) return false;
      if (selectedStatuses.size > 0 && !selectedStatuses.has(item.status)) return false;
      if (selectedTypes.size > 0 && !selectedTypes.has(item.type)) return false;
      if (effectiveCategories.size > 0 && !effectiveCategories.has(item.category)) return false;
      return true;
    });
    // Recompute summary from filtered items
    const planned = items.filter(i => i.status === 'planned').reduce((s, i) => s + (i.type === 'expense' ? -i.amount : i.amount), 0);
    const approved = items.filter(i => i.status === 'approved').reduce((s, i) => s + (i.type === 'expense' ? -i.amount : i.amount), 0);
    const completed = items.filter(i => i.status === 'completed').reduce((s, i) => s + (i.type === 'expense' ? -i.amount : i.amount), 0);
    const totalRevenue = items.filter(i => i.type === 'revenue' && i.status !== 'cancelled').reduce((s, i) => s + i.amount, 0);
    const totalExpenses = items.filter(i => i.type === 'expense' && i.status !== 'cancelled').reduce((s, i) => s + i.amount, 0);
    return {
      items,
      summary: {
        planned_total: Math.abs(planned),
        approved_total: Math.abs(approved),
        completed_total: Math.abs(completed),
        net: totalRevenue - totalExpenses,
        total_revenue: totalRevenue,
        total_expenses: totalExpenses,
        count: items.length,
      },
    };
  }, [rawData, effectiveEntities, selectedStatuses, selectedTypes, effectiveCategories]);

  const openNewDialog = (type: 'expense' | 'revenue') => {
    setDialogType(type);
    setEditingItem(null);
    setDialogOpen(true);
  };

  const openEditDialog = (item: CashflowItem) => {
    setDialogType(item.type as 'expense' | 'revenue');
    setEditingItem(item);
    setDialogOpen(true);
  };

  const handleStatusChange = async (id: string, newStatus: string, item?: CashflowItem) => {
    // Intercept 'completed' to show completion dialog
    if (newStatus === 'completed' && item) {
      setCompletionItem(item);
      return;
    }
    try {
      const res = await fetch('/api/fin/cashflow-items', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: newStatus }),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Failed to update status');
        return;
      }
      await refresh();
    } catch { /* ignore */ }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this planned item?')) return;
    try {
      const res = await fetch('/api/fin/cashflow-items', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Failed to delete');
        return;
      }
      await refresh();
    } catch { /* ignore */ }
  };

  if (loading && !data) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const summary = data?.summary;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Wallet size={24} /> Transactions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Expense and revenue intake with status tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-2" onClick={() => openNewDialog('revenue')}>
            <ArrowUpCircle size={14} className="text-emerald-600" /> + Revenue
          </Button>
          <Button size="sm" className="gap-2" onClick={() => openNewDialog('expense')}>
            <ArrowDownCircle size={14} /> + Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="shadow-xs">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase mb-1">Planned</div>
              <div className="text-xl font-bold text-amber-600">{formatCurrency(summary.planned_total)}</div>
            </CardContent>
          </Card>
          <Card className="shadow-xs">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase mb-1">Approved</div>
              <div className="text-xl font-bold text-blue-600">{formatCurrency(summary.approved_total)}</div>
            </CardContent>
          </Card>
          <Card className="shadow-xs">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase mb-1">Completed</div>
              <div className="text-xl font-bold text-emerald-600">{formatCurrency(summary.completed_total)}</div>
            </CardContent>
          </Card>
          <Card className="shadow-xs">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase mb-1">Net (Rev - Exp)</div>
              <div className={`text-xl font-bold ${summary.net >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {formatCurrency(summary.net)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* Row 1 — multi-select filters + date preset */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MultiSelectFilter
              label="Entities"
              options={entityOptions}
              selected={effectiveEntities}
              onChange={setSelectedEntities}
            />
            <MultiSelectFilter
              label="Status"
              options={ALL_STATUSES.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
            />
            <MultiSelectFilter
              label="Type"
              options={ALL_TYPES.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
              selected={selectedTypes}
              onChange={setSelectedTypes}
            />
            <MultiSelectFilter
              label="Category"
              options={categoryOptions}
              selected={effectiveCategories}
              onChange={setSelectedCategories}
            />
            <Select value={datePreset} onValueChange={v => applyDatePreset(v as DateRangePresetKey)}>
              <SelectTrigger><SelectValue placeholder="Date range" /></SelectTrigger>
              <SelectContent>
                {DATE_RANGE_PRESETS.map(p => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row 2 — from / to / search */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setDatePreset('custom'); }}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setDatePreset('custom'); }}
              />
            </div>
            <div className="relative self-end">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Date</TableHead>
                <TableHead className="text-xs">Entity</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">Description</TableHead>
                <TableHead className="text-xs text-right">Amount</TableHead>
                <TableHead className="text-xs">Counterparty</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.items || []).map(item => {
                const style = STATUS_STYLES[item.status] || STATUS_STYLES.planned;
                return (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => setDetailItem(item)}
                  >
                    <TableCell className="text-sm">{formatDate(item.expected_date)}</TableCell>
                    <TableCell className="text-sm">{item.entity_name}</TableCell>
                    <TableCell>
                      <Badge variant={item.type === 'revenue' ? 'default' : 'destructive'} className="text-[10px]">
                        {item.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{item.category}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">
                      <span className="inline-flex items-center gap-1">
                        {item.description || '-'}
                        {(item.document_url || item.document_name) && (
                          <FileText size={12} className={item.document_url ? 'text-blue-500' : 'text-muted-foreground'} />
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-right font-mono">
                      <span className={item.type === 'revenue' ? 'text-emerald-600' : 'text-red-500'}>
                        {item.type === 'revenue' ? '+' : '-'}{formatCurrency(item.amount)} {item.currency}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{item.counterparty || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge variant={style.variant} className={`text-[10px] ${style.className}`}>
                          {item.status}
                        </Badge>
                        {item.source_tag && SOURCE_TAG_STYLES[item.source_tag] && (
                          <Badge variant="outline" className={`text-[9px] px-1 py-0 ${SOURCE_TAG_STYLES[item.source_tag].className}`}>
                            {SOURCE_TAG_STYLES[item.source_tag].label}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailItem(item)}>
                            <Eye size={14} className="mr-2" /> View Details
                          </DropdownMenuItem>
                          {item.status === 'planned' && (
                            <>
                              <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'approved')}>
                                <Check size={14} className="mr-2 text-blue-600" /> Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(item)}>
                                <Edit3 size={14} className="mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'cancelled')}>
                                <Ban size={14} className="mr-2 text-gray-500" /> Cancel
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600">
                                <Trash2 size={14} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                          {item.status === 'approved' && (
                            <>
                              <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'completed', item)}>
                                <Check size={14} className="mr-2 text-emerald-600" /> Complete
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(item)}>
                                <Edit3 size={14} className="mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'cancelled')}>
                                <Ban size={14} className="mr-2 text-gray-500" /> Cancel
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!data?.items || data.items.length === 0) && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No items found. Click "+ Expense" or "+ Revenue" to start adding.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Intake Dialog */}
      {dialogOpen && (
        <IntakeDialog
          type={dialogType}
          editItem={editingItem}
          entities={entities || []}
          categories={categories || []}
          onClose={() => { setDialogOpen(false); setEditingItem(null); }}
          onSaved={() => { setDialogOpen(false); setEditingItem(null); refresh(); }}
        />
      )}

      {/* Detail Dialog */}
      {detailItem && (
        <DetailDialog
          item={detailItem}
          onClose={() => setDetailItem(null)}
        />
      )}

      {/* Completion Dialog */}
      {completionItem && (
        <CompletionDialog
          item={completionItem}
          onClose={() => setCompletionItem(null)}
          onCompleted={() => { setCompletionItem(null); refresh(); }}
        />
      )}
    </div>
  );
}

/* ---------- Intake Dialog ---------- */
function IntakeDialog({
  type: initialType, editItem, entities, categories, onClose, onSaved,
}: {
  type: 'expense' | 'revenue';
  editItem: CashflowItem | null;
  entities: Entity[];
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: bankAccounts } = useFetch<BankAccount[]>('/api/fin/bank-accounts');

  const [type, setType] = useState(editItem?.type || initialType);
  const [entityId, setEntityId] = useState(editItem?.entity_id || '');
  const [category, setCategory] = useState(editItem?.category || '');
  const [amount, setAmount] = useState(editItem ? String(editItem.amount) : '');
  const [currency, setCurrency] = useState(editItem?.currency || 'EUR');
  const [expectedDate, setExpectedDate] = useState(editItem?.expected_date || new Date().toISOString().split('T')[0]);
  const [counterparty, setCounterparty] = useState(editItem?.counterparty || '');
  const [bankAccountId, setBankAccountId] = useState(editItem?.bank_account_id || '');
  const [description, setDescription] = useState(editItem?.description || '');
  const [documentName, setDocumentName] = useState(editItem?.document_name || '');
  const [documentUrl, setDocumentUrl] = useState(editItem?.document_url || '');
  const [notes, setNotes] = useState(editItem?.notes || '');
  const [paymentMethod, setPaymentMethod] = useState(editItem?.payment_method || 'fiat');
  const [isIntercompany, setIsIntercompany] = useState(editItem?.is_intercompany || false);
  const [intercompanyEntityId, setIntercompanyEntityId] = useState(editItem?.intercompany_entity_id || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file: File) => {
    setUploading(true);
    setDocumentName(file.name);
    try {
      const res = await fetch(`/api/fin/upload-url?filename=${encodeURIComponent(file.name)}`, {
        method: 'PUT',
        body: file,
      });
      const data = await res.json();
      setDocumentUrl(data.url || '');
    } catch {
      setDocumentUrl('');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  // Filter categories by type
  const filteredCategories = useMemo(() => {
    const catType = type === 'revenue' ? 'income' : 'expense';
    return categories.filter(c => c.type === catType || c.type === 'transfer');
  }, [categories, type]);

  // Filter bank accounts by entity
  const filteredAccounts = useMemo(() => {
    if (!bankAccounts || !entityId) return [];
    return bankAccounts.filter(b => b.entity_id === entityId);
  }, [bankAccounts, entityId]);

  const handleSave = async () => {
    if (!entityId || !category || !amount || !expectedDate) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        entity_id: entityId,
        type,
        category,
        amount: parseFloat(amount),
        currency,
        expected_date: expectedDate,
        counterparty: counterparty || null,
        bank_account_id: bankAccountId || null,
        description: description || null,
        document_name: documentName || null,
        document_url: documentUrl || null,
        notes: notes || null,
        created_by: 'dashboard',
        payment_method: paymentMethod,
        is_intercompany: isIntercompany,
        intercompany_entity_id: isIntercompany && intercompanyEntityId ? intercompanyEntityId : null,
      };

      const method = editItem ? 'PATCH' : 'POST';
      if (editItem) body.id = editItem.id;

      const res = await fetch('/api/fin/cashflow-items', {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Failed to save');
        return;
      }
      onSaved();
    } catch {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editItem ? 'Edit' : 'New'} {type === 'revenue' ? 'Revenue' : 'Expense'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={v => { setType(v); setCategory(''); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Revenue</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Entity *</Label>
              <Select value={entityId || '__none__'} onValueChange={v => { setEntityId(v === '__none__' ? '' : v); setBankAccountId(''); }}>
                <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select...</SelectItem>
                  {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.code} - {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Category *</Label>
            <Select value={category || '__none__'} onValueChange={v => setCategory(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Select...</SelectItem>
                {filteredCategories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Expected Date *</Label>
              <Input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Counterparty</Label>
              <Input value={counterparty} onChange={e => setCounterparty(e.target.value)} placeholder="Optional" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Bank Account</Label>
            <Select value={bankAccountId || '__none__'} onValueChange={v => setBankAccountId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {filteredAccounts.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.bank_name} ({b.currency})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Payment Method & Intercompany */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fiat">Fiat</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Inter-company</Label>
              <div className="flex items-center gap-3 h-10">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isIntercompany}
                    onChange={e => {
                      setIsIntercompany(e.target.checked);
                      if (!e.target.checked) setIntercompanyEntityId('');
                    }}
                    className="rounded border-border"
                  />
                  <span className="text-sm">Inter-company transfer</span>
                </label>
              </div>
            </div>
          </div>

          {isIntercompany && (
            <div className="space-y-1.5">
              <Label>{type === 'expense' ? 'To Entity' : 'From Entity'}</Label>
              <Select value={intercompanyEntityId || '__none__'} onValueChange={v => setIntercompanyEntityId(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select counterparty entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select...</SelectItem>
                  {entities.filter(e => e.id !== entityId).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.code} - {e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>Supporting Document</Label>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
              } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg"
                className="hidden"
              />
              {uploading ? (
                <p className="text-sm text-muted-foreground animate-pulse">Uploading...</p>
              ) : documentName ? (
                <div className="flex items-center justify-center gap-2">
                  {documentUrl ? (
                    <Badge variant="outline" className="text-xs text-green-600">Uploaded: {documentName}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">{documentName}</Badge>
                  )}
                  <button className="text-xs text-muted-foreground hover:text-foreground" onClick={e => { e.stopPropagation(); setDocumentName(''); setDocumentUrl(''); }}>✕</button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Drop file here or <span className="text-primary font-medium">click to browse</span>
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" rows={2} />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !entityId || !category || !amount || !expectedDate}>
              {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Completion Dialog ---------- */
function CompletionDialog({ item, onClose, onCompleted }: {
  item: CashflowItem;
  onClose: () => void;
  onCompleted: () => void;
}) {
  const [actualAmount, setActualAmount] = useState(String(item.amount));
  const [actualCurrency, setActualCurrency] = useState(item.currency);
  const [completionNotes, setCompletionNotes] = useState(item.notes || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState(item.document_url || '');
  const [invoiceName, setInvoiceName] = useState(item.document_name || '');
  const [parsedData, setParsedData] = useState<Record<string, unknown> | null>(null);
  const [parseError, setParseError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file: File) => {
    setUploading(true);
    setInvoiceName(file.name);
    try {
      const res = await fetch(`/api/fin/upload-url?filename=${encodeURIComponent(file.name)}`, {
        method: 'PUT',
        body: file,
      });
      const data = await res.json();
      setInvoiceUrl(data.url || '');
    } catch {
      setInvoiceUrl('');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleParseInvoice = async () => {
    if (!invoiceUrl) return;
    setParsing(true);
    setParseError('');
    try {
      const res = await fetch('/api/fin/parse-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_url: invoiceUrl }),
      });
      const data = await res.json();
      if (!res.ok) {
        setParseError(data.error || 'Failed to parse');
        return;
      }
      if (data.parsed) {
        setParsedData(data.parsed);
      } else {
        setParseError('Could not extract data from invoice');
      }
    } catch {
      setParseError('Failed to parse invoice');
    } finally {
      setParsing(false);
    }
  };

  const useParsedAmount = () => {
    if (parsedData) {
      if (parsedData.amount != null) setActualAmount(String(parsedData.amount));
      if (parsedData.currency) setActualCurrency(parsedData.currency as string);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        id: item.id,
        status: 'completed',
        actual_amount: parseFloat(actualAmount) || item.amount,
        actual_currency: actualCurrency,
        notes: completionNotes || null,
        document_name: invoiceName || null,
        document_url: invoiceUrl || null,
        invoice_parsed_data: parsedData || null,
      };
      const res = await fetch('/api/fin/cashflow-items', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Failed to complete');
        return;
      }
      onCompleted();
    } catch {
      alert('Failed to complete');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Complete Expense</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Planned amount */}
          <div className="rounded-lg bg-muted/50 p-3">
            <div className="text-xs text-muted-foreground uppercase mb-1">Planned Amount</div>
            <div className="text-lg font-bold">
              {item.currency} {formatCurrency(item.amount)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {item.category} &middot; {item.counterparty || 'No counterparty'} &middot; {formatDate(item.expected_date)}
            </div>
          </div>

          {/* Actual amount */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Actual Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={actualAmount}
                onChange={e => setActualAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={actualCurrency} onValueChange={setActualCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Variance indicator */}
          {actualAmount && parseFloat(actualAmount) !== item.amount && (
            <div className={`text-xs px-2 py-1 rounded ${parseFloat(actualAmount) < item.amount ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              Variance: {parseFloat(actualAmount) < item.amount ? '-' : '+'}{formatCurrency(Math.abs(parseFloat(actualAmount) - item.amount))} {actualCurrency}
              ({((parseFloat(actualAmount) - item.amount) / item.amount * 100).toFixed(1)}%)
            </div>
          )}

          {/* Invoice upload */}
          <div className="space-y-1.5">
            <Label>Upload Invoice</Label>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
              } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
              onClick={() => document.getElementById('completion-file-input')?.click()}
            >
              <input
                id="completion-file-input"
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="hidden"
              />
              {uploading ? (
                <p className="text-sm text-muted-foreground animate-pulse">Uploading...</p>
              ) : invoiceName ? (
                <div className="flex items-center justify-center gap-2">
                  <Badge variant="outline" className="text-xs text-green-600">
                    <FileText size={12} className="mr-1" /> {invoiceName}
                  </Badge>
                  <button className="text-xs text-muted-foreground hover:text-foreground" onClick={e => { e.stopPropagation(); setInvoiceName(''); setInvoiceUrl(''); setParsedData(null); }}>
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  <Upload size={14} className="inline mr-1" />
                  Drop invoice here or <span className="text-primary font-medium">click to browse</span>
                </p>
              )}
            </div>
          </div>

          {/* Parse button */}
          {invoiceUrl && !parsedData && (
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={handleParseInvoice}
              disabled={parsing}
            >
              {parsing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {parsing ? 'Parsing invoice...' : 'Parse Invoice with AI'}
            </Button>
          )}

          {parseError && (
            <p className="text-xs text-red-500">{parseError}</p>
          )}

          {/* Parsed results */}
          {parsedData && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <Sparkles size={12} /> AI Parsed Result
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {parsedData.amount != null && (
                  <div><span className="text-muted-foreground">Amount:</span> <span className="font-mono font-medium">{String(parsedData.amount)}</span></div>
                )}
                {parsedData.currency && (
                  <div><span className="text-muted-foreground">Currency:</span> <span className="font-medium">{String(parsedData.currency)}</span></div>
                )}
                {parsedData.vendor && (
                  <div><span className="text-muted-foreground">Vendor:</span> <span className="font-medium">{String(parsedData.vendor)}</span></div>
                )}
                {parsedData.date && (
                  <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{String(parsedData.date)}</span></div>
                )}
                {parsedData.invoice_number && (
                  <div className="col-span-2"><span className="text-muted-foreground">Invoice #:</span> <span className="font-medium">{String(parsedData.invoice_number)}</span></div>
                )}
                {parsedData.description && (
                  <div className="col-span-2"><span className="text-muted-foreground">Description:</span> <span className="font-medium">{String(parsedData.description)}</span></div>
                )}
              </div>
              <Button variant="outline" size="sm" className="w-full mt-2 text-xs" onClick={useParsedAmount}>
                Use Parsed Amount
              </Button>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={completionNotes} onChange={e => setCompletionNotes(e.target.value)} placeholder="Optional completion notes" rows={2} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button onClick={handleComplete} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? 'Completing...' : 'Complete'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Detail Dialog ---------- */
function DetailDialog({ item, onClose }: { item: CashflowItem; onClose: () => void }) {
  const fields: { label: string; value: string | null }[] = [
    { label: 'Type', value: item.type },
    { label: 'Entity', value: item.entity_name },
    { label: 'Category', value: item.category },
    { label: 'Planned Amount', value: `${formatCurrency(item.amount)} ${item.currency}` },
    { label: 'Actual Amount', value: item.actual_amount != null ? `${formatCurrency(item.actual_amount)} ${item.actual_currency || item.currency}` : null },
    { label: 'Source', value: item.source_tag ? (SOURCE_TAG_STYLES[item.source_tag]?.label || item.source_tag) : null },
    { label: 'Expected Date', value: formatDate(item.expected_date) },
    { label: 'Actual Date', value: item.actual_date ? formatDate(item.actual_date) : null },
    { label: 'Counterparty', value: item.counterparty },
    { label: 'Bank Account', value: item.bank_account_name },
    { label: 'Status', value: item.status },
    { label: 'Description', value: item.description },
    { label: 'Notes', value: item.notes },
    { label: 'Created By', value: item.created_by },
    { label: 'Approved By', value: item.approved_by },
    { label: 'Completed At', value: item.completed_at ? new Date(item.completed_at).toLocaleString() : null },
    { label: 'Payment Method', value: item.payment_method || 'fiat' },
    { label: 'Inter-company', value: item.is_intercompany ? 'Yes' : null },
    { label: 'IC Entity', value: item.intercompany_entity_id },
    { label: 'Created At', value: new Date(item.created_at).toLocaleString() },
  ];

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Item Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {fields.map(f => (
            f.value ? (
              <div key={f.label} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{f.label}</span>
                <span className="font-medium text-right max-w-[60%]">{f.value}</span>
              </div>
            ) : null
          ))}
          {/* Document link/name */}
          {(item.document_name || item.document_url) && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Document</span>
              <span className="font-medium text-right max-w-[60%]">
                {item.document_url ? (
                  <a
                    href={item.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <FileText size={14} />
                    {item.document_name || 'View Document'}
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <FileText size={14} />
                    {item.document_name}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Multi-select filter component ---------- */
function MultiSelectFilter({ label, options, selected, onChange }: {
  label: string;
  options: { value: string; label: string }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const allSelected = selected.size === options.length;
  const noneSelected = selected.size === 0;

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value); else next.add(value);
    onChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-between text-sm font-normal h-9 w-full">
          <span className="truncate">
            {allSelected || noneSelected ? `All ${label}` : `${label} (${selected.size}/${options.length})`}
          </span>
          <ChevronDown size={14} className="ml-1 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="flex items-center justify-between px-2 pb-2 border-b mb-1">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <div className="flex gap-1">
            <button className="text-[10px] text-blue-600 hover:underline" onClick={() => onChange(new Set(options.map(o => o.value)))}>All</button>
            <span className="text-[10px] text-muted-foreground">/</span>
            <button className="text-[10px] text-blue-600 hover:underline" onClick={() => onChange(new Set())}>None</button>
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {options.map(opt => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted cursor-pointer text-sm"
            >
              <Checkbox
                checked={selected.has(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              />
              <span className="truncate">{opt.label}</span>
            </label>
          ))}
          {options.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-3">No options</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
